import { createStatusOptions } from '@/config/statusOptions';

// `userrole`, plus which roles each role may grant — mirrors iscc-backend's canInviteRole.

export const USER_ROLE = Object.freeze({
  superAdmin: 'SUPER_ADMIN',
  admin: 'ADMIN',
  siteManager: 'SITE_MANAGER',
  member: 'MEMBER',
  owner: 'OWNER',
  audit: 'AUDIT',
  external: 'EXTERNAL',
});

export const USER_ROLE_LABEL_KEYS = Object.freeze({
  [USER_ROLE.superAdmin]: 'ROLE_SUPER_ADMIN',
  [USER_ROLE.admin]: 'ROLE_ADMIN',
  [USER_ROLE.siteManager]: 'ROLE_SITE_MANAGER',
  [USER_ROLE.member]: 'ROLE_MEMBER',
  [USER_ROLE.owner]: 'ROLE_OWNER',
  [USER_ROLE.audit]: 'ROLE_AUDIT',
  [USER_ROLE.external]: 'ROLE_EXTERNAL',
});

export const { statusLabel: userRoleLabel } = createStatusOptions(USER_ROLE_LABEL_KEYS);

const ROLE_DESCRIPTION_KEYS = Object.freeze({
  [USER_ROLE.superAdmin]: 'ROLE_SUPER_ADMIN_DESCRIPTION',
  [USER_ROLE.admin]: 'ROLE_ADMIN_DESCRIPTION',
  [USER_ROLE.siteManager]: 'ROLE_SITE_MANAGER_DESCRIPTION',
});

// The roles the Add / Edit user form and the Access type filter offer, in design order.
export const FORM_ROLES = Object.freeze([
  USER_ROLE.superAdmin,
  USER_ROLE.admin,
  USER_ROLE.siteManager,
]);

const INVITABLE_ROLES = Object.freeze({
  [USER_ROLE.superAdmin]: [
    USER_ROLE.superAdmin,
    USER_ROLE.admin,
    USER_ROLE.siteManager,
    USER_ROLE.external,
  ],
  [USER_ROLE.admin]: [USER_ROLE.siteManager, USER_ROLE.admin, USER_ROLE.external],
  [USER_ROLE.siteManager]: [USER_ROLE.siteManager, USER_ROLE.external],
});

export const canInviteRole = (inviterRole, role) =>
  (INVITABLE_ROLES[inviterRole] ?? []).includes(role);

export const canInviteAny = (inviterRole) => Boolean(INVITABLE_ROLES[inviterRole]?.length);

// An unchanged role is never re-checked server-side, so the user's own role stays selectable.
export const buildRoleOptions = (t, inviterRole, currentRole) =>
  FORM_ROLES.filter((role) => role === currentRole || canInviteRole(inviterRole, role)).map(
    (role) => ({
      value: role,
      label: t(USER_ROLE_LABEL_KEYS[role]),
      subLabel: t(ROLE_DESCRIPTION_KEYS[role]),
    }),
  );

export const buildRoleFilterOptions = (t) =>
  FORM_ROLES.map((role) => ({ value: role, label: t(USER_ROLE_LABEL_KEYS[role]) }));

// A super admin sees every site; an admin picks several, a site manager exactly one.
export const roleHasAllSites = (role) => role === USER_ROLE.superAdmin;
export const rolePicksManySites = (role) => role === USER_ROLE.admin;
export const rolePicksOneSite = (role) => role === USER_ROLE.siteManager;
